import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import AuthModal from './AuthModal';
import {
  Check,
  X,
  Star,
  Zap,
  Crown,
  ArrowRight,
  Shield,
  Sparkles
} from 'lucide-react';

const PricingPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [showAuthModal, setShowAuthModal] = useState(false);

  const plans = [
    {
      id: 'free',
      name: 'Free',
      price: '₹0',
      period: 'forever',
      description: 'Get started with basic resume analysis',
      icon: Zap,
      color: 'gray',
      features: [
        'Resume analysis preview',
        'ATS score up to 67%',
        'Basic improvement tips',
        'Limited keyword matching',
        'Demo mode features'
      ],
      limitations: [
        'No full optimization',
        'No downloads',
        'No history tracking',
        'Limited AI insights'
      ],
      cta: 'Start Free',
      popular: false
    },
    {
      id: 'basic',
      name: 'Basic',
      price: '₹199',
      period: 'one-time',
      description: 'Perfect for job seekers who want optimized resumes',
      icon: Star,
      color: 'blue',
      features: [
        'Full AI resume optimization',
        'Complete ATS score analysis',
        '3 resume optimizations',
        'All resume templates',
        'PDF & DOCX downloads',
        'Keyword optimization',
        'Industry-specific tips'
      ],
      limitations: [
        'Limited to 3 resumes',
        'No cover letter generation',
        'No interview preparation'
      ],
      cta: 'Get Basic',
      popular: true,
      savings: 'Most Popular'
    },
    {
      id: 'premium',
      name: 'Premium',
      price: '₹499',
      period: 'one-time',
      description: 'Complete career optimization suite',
      icon: Crown,
      color: 'purple',
      features: [
        'Unlimited resume optimizations',
        'AI cover letter generation',
        'Interview preparation tools',
        'Job matching assistance',
        'Priority AI processing',
        'Advanced analytics',
        'Career guidance',
        'Premium templates',
        'Email support'
      ],
      limitations: [],
      cta: 'Go Premium',
      popular: false,
      savings: 'Best Value'
    }
  ];

  const handlePlanSelect = (planId) => {
    if (user) {
      // Redirect to payment or resume optimizer
      if (planId === 'free') {
        navigate('/resume-optimizer');
      } else {
        navigate('/resume-optimizer', { state: { selectedPlan: planId } });
      }
    } else {
      // Show auth modal for non-authenticated users
      setShowAuthModal(true);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
      {/* Header */}
      <div className="bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div className="flex items-center">
              <button
                onClick={() => navigate('/')}
                className="text-2xl font-bold text-slate-900 hover:text-blue-600 transition-colors"
              >
                Shortlistly
              </button>
            </div>
            <div className="flex items-center space-x-4">
              <button
                onClick={() => navigate('/')}
                className="text-slate-600 hover:text-slate-900 transition-colors"
              >
                Home
              </button>
              {user ? (
                <button
                  onClick={() => navigate('/dashboard')}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  Dashboard
                </button>
              ) : (
                <button
                  onClick={() => setShowAuthModal(true)}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  Sign In
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        {/* Hero Section */}
        <div className="text-center mb-16">
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-5xl font-bold text-slate-900 mb-6"
          >
            Why settle for a <span className="text-red-600">67% resume</span>?
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="text-xl text-slate-600 mb-8 max-w-3xl mx-auto"
          >
            Unlock your resume's full potential with AI-powered optimization. 
            Transform your career prospects with resumes that actually get interviews.
          </motion.p>
          
          {/* Value Proposition */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-gradient-to-r from-red-50 to-orange-50 border border-red-200 rounded-2xl p-6 max-w-2xl mx-auto mb-12"
          >
            <div className="flex items-center justify-center mb-4">
              <Shield className="w-8 h-8 text-red-600 mr-3" />
              <h3 className="text-xl font-bold text-red-900">Don't Let Your Resume Hold You Back</h3>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
              <div className="text-center">
                <div className="text-2xl font-bold text-red-600">73%</div>
                <div className="text-red-700">Rejection rate for 67% ATS scores</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-red-600">5 sec</div>
                <div className="text-red-700">Average recruiter review time</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-red-600">90%</div>
                <div className="text-red-700">Companies use ATS screening</div>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Pricing Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16">
          {plans.map((plan, index) => (
            <motion.div
              key={plan.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 * index }}
              className={`relative bg-white rounded-3xl shadow-lg border-2 transition-all duration-300 hover:shadow-xl ${
                plan.popular 
                  ? 'border-blue-500 transform scale-105' 
                  : 'border-slate-200 hover:border-blue-300'
              }`}
            >
              {/* Popular Badge */}
              {plan.popular && (
                <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                  <div className="bg-blue-600 text-white px-6 py-2 rounded-full text-sm font-semibold">
                    {plan.savings}
                  </div>
                </div>
              )}

              <div className="p-8">
                {/* Plan Header */}
                <div className="text-center mb-8">
                  <div className={`w-16 h-16 mx-auto mb-4 rounded-2xl bg-${plan.color}-100 flex items-center justify-center`}>
                    <plan.icon className={`w-8 h-8 text-${plan.color}-600`} />
                  </div>
                  <h3 className="text-2xl font-bold text-slate-900 mb-2">{plan.name}</h3>
                  <div className="text-4xl font-bold text-slate-900 mb-1">
                    {plan.price}
                    <span className="text-lg font-normal text-slate-500">/{plan.period}</span>
                  </div>
                  <p className="text-slate-600">{plan.description}</p>
                </div>

                {/* Features */}
                <div className="space-y-4 mb-8">
                  {plan.features.map((feature, idx) => (
                    <div key={idx} className="flex items-center">
                      <Check className="w-5 h-5 text-green-500 mr-3 flex-shrink-0" />
                      <span className="text-slate-700">{feature}</span>
                    </div>
                  ))}
                  
                  {plan.limitations.map((limitation, idx) => (
                    <div key={idx} className="flex items-center opacity-60">
                      <X className="w-5 h-5 text-slate-400 mr-3 flex-shrink-0" />
                      <span className="text-slate-500">{limitation}</span>
                    </div>
                  ))}
                </div>

                {/* CTA Button */}
                <button
                  onClick={() => handlePlanSelect(plan.id)}
                  className={`w-full py-4 px-6 rounded-xl font-semibold transition-all duration-300 flex items-center justify-center ${
                    plan.popular
                      ? 'bg-gradient-to-r from-blue-600 to-purple-600 text-white hover:shadow-lg transform hover:scale-105'
                      : plan.id === 'premium'
                      ? 'bg-gradient-to-r from-purple-600 to-pink-600 text-white hover:shadow-lg'
                      : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                  }`}
                >
                  <span>{plan.cta}</span>
                  <ArrowRight className="w-5 h-5 ml-2" />
                </button>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Success Stories */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="bg-white rounded-3xl p-8 shadow-lg mb-16"
        >
          <div className="text-center mb-8">
            <h2 className="text-3xl font-bold text-slate-900 mb-4">Success Stories</h2>
            <p className="text-slate-600">See how our AI optimization transforms careers</p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="text-4xl font-bold text-green-600 mb-2">89%</div>
              <div className="text-slate-700 font-medium mb-1">Average ATS Score</div>
              <div className="text-slate-500 text-sm">After optimization</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold text-blue-600 mb-2">3.2x</div>
              <div className="text-slate-700 font-medium mb-1">More Interviews</div>
              <div className="text-slate-500 text-sm">Compared to original resume</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold text-purple-600 mb-2">72h</div>
              <div className="text-slate-700 font-medium mb-1">Average Response</div>
              <div className="text-slate-500 text-sm">From job applications</div>
            </div>
          </div>
        </motion.div>

        {/* FAQ or Additional Info */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="text-center"
        >
          <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-2xl p-8">
            <Sparkles className="w-12 h-12 text-blue-600 mx-auto mb-4" />
            <h3 className="text-2xl font-bold text-slate-900 mb-4">Ready to Transform Your Career?</h3>
            <p className="text-slate-600 mb-6">
              Join thousands of professionals who have boosted their career prospects with AI-optimized resumes.
            </p>
            <button
              onClick={() => handlePlanSelect('basic')}
              className="px-8 py-4 bg-gradient-to-r from-blue-600 to-purple-600 text-white font-semibold rounded-xl hover:shadow-lg transition-all duration-300"
            >
              Start Optimizing Now
            </button>
          </div>
        </motion.div>
      </div>

      {/* Auth Modal */}
      <AuthModal
        isOpen={showAuthModal}
        onClose={() => setShowAuthModal(false)}
        onSuccess={() => {
          setShowAuthModal(false);
          navigate('/dashboard');
        }}
      />
    </div>
  );
};

export default PricingPage;